import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  applyTemporalPattern,
  applyThousandsSeparator,
  DecimalFieldFormat,
  formatCnpj,
  formatCpf,
  normalizeCpaGridConfig,
  parseDateValue,
  TemporalFieldFormat
} from './cpa-grid-bridge-format.utils';
import { CpaSelectFallbackController } from './cpa-grid-bridge-select-fallback';
import { CpaFieldMaskController } from './cpa-grid-bridge-field-mask';
import { CpaSelectSyncController } from './cpa-grid-bridge-select-sync';
import { CpaRequiredValidationController } from './cpa-grid-bridge-required-validation';

/**
 * Injector singleton do micro-app AngularJS bootstrapado para o cpa-grid.
 * Criado uma unica vez por ciclo de vida da aplicacao.
 */
let cpaInjector: any = null;
const CPA_BRIDGE_SCOPE_CLASS = 'cpa-grid-scope';

@Component({
  selector: 'app-cpa-grid-bridge',
  template: `
    <div [class]="scopeClass">
      <div #host></div>
    </div>
    <div *ngIf="!angularJsDisponivel"
      class="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 px-4 py-3
             text-sm text-gray-500 dark:text-gray-400 text-center">
      Campos adicionais indisponiveis.
    </div>
  `
})
export class CpaGridBridgeComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() config: any = null;
  @Output() requiredFieldsValidChange = new EventEmitter<boolean>();
  @ViewChild('host') hostRef!: ElementRef<HTMLElement>;

  readonly scopeClass = CPA_BRIDGE_SCOPE_CLASS;
  angularJsDisponivel = false;

  private scope: any = null;
  private compiledElement: any = null;
  private ng: any = null;
  private renderCount = 0;
  private selectValueByTitle: Record<string, string> = {};
  private maskTypeByTitle: Record<string, 'CPF' | 'CNPJ' | 'CPF_CNPJ'> = {};
  private temporalTypeByTitle: Record<string, 'DATE' | 'TIME' | 'DATETIME'> = {};
  private temporalFormatByTitle: Record<string, TemporalFieldFormat> = {};
  private decimalFormatByTitle: Record<string, DecimalFieldFormat> = {};
  private readonly selectFallback = new CpaSelectFallbackController({
    getScope: () => this.scope,
    getNg: () => this.ng
  });
  private readonly fieldMask = new CpaFieldMaskController({
    getScope: () => this.scope,
    getNg: () => this.ng
  });
  private readonly selectSync = new CpaSelectSyncController();
  private readonly requiredValidation = new CpaRequiredValidationController(
    (valid) => this.requiredFieldsValidChange.emit(valid)
  );

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.ng = (window as any).angular;
    if (!this.ng) {
      return;
    }

    if (!cpaInjector) {
      cpaInjector = this.bootstrapCpaModule(this.ng);
    }

    if (!cpaInjector) {
      return;
    }

    this.angularJsDisponivel = true;
    this.cdr.detectChanges();

    const $rootScope = cpaInjector.get('$rootScope');
    this.scope = $rootScope.$new(true);
    this.renderGrid();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && this.scope) {
      this.renderGrid();
    }
  }

  ngOnDestroy(): void {
    if (this.scope) {
      this.scope.$destroy();
      this.scope = null;
    }

    this.selectFallback.dispose();

    this.destroyCompiledElement();
  }

  private bootstrapCpaModule(ng: any): any {
    try {
      ng.module('cpa-pwa-bridge', ['cpa-components'])
        .service('bfNumberServices', function() { /* stub */ });

      const rootEl = document.createElement('div');
      rootEl.setAttribute('data-cpa-bridge-root', '');
      rootEl.style.display = 'none';
      document.body.appendChild(rootEl);

      const injector = ng.bootstrap(rootEl, ['cpa-pwa-bridge']);
      return injector;
    } catch (e) {
      return null;
    }
  }

  private renderGrid(): void {
    if (!this.scope || !cpaInjector || !this.ng) {
      return;
    }

    this.destroyCompiledElement();
    this.renderCount += 1;

    this.scope.config = this.normalizeConfig(this.config);
    const $compile = cpaInjector.get('$compile');
    const $rootScope = cpaInjector.get('$rootScope');

    const template = '<form name="cpaBridgeForm"><cpa-grid config="config"></cpa-grid></form>';
    this.compiledElement = $compile(template)(this.scope);
    this.ng.element(this.hostRef.nativeElement).append(this.compiledElement);

    if (!$rootScope.$$phase) {
      this.scope.$applyAsync();
    }

    setTimeout(() => this.syncSelectVisualState(), 0);
    setTimeout(() => this.syncSelectVisualState(), 50);
    setTimeout(() => this.enhanceMultipleSelects(), 0);
    setTimeout(() => this.enhanceMultipleSelects(), 80);
    setTimeout(() => this.renderMultipleFallbackChips(), 120);
    setTimeout(() => this.renderSingleSelectFallback(), 0);
    setTimeout(() => this.renderSingleSelectFallback(), 100);
    setTimeout(() => this.enhanceDateInputs(), 0);
    setTimeout(() => this.enhanceDateInputs(), 100);
    setTimeout(() => this.annotateRequiredToggleTitles(), 120);
    setTimeout(() => this.annotateRequiredToggleTitles(), 260);
    setTimeout(() => this.bindRequiredValidationVisualState(), 140);
    setTimeout(() => this.bindRequiredValidationVisualState(), 260);
    setTimeout(() => this.enhanceCpfCnpjInputs(), 0);
    setTimeout(() => this.enhanceCpfCnpjInputs(), 120);
    setTimeout(() => this.enhanceTemporalTextMasks(), 0);
    setTimeout(() => this.enhanceTemporalTextMasks(), 120);
    setTimeout(() => this.enhanceDecimalInputs(), 0);
    setTimeout(() => this.enhanceDecimalInputs(), 120);
    setTimeout(() => this.enhanceIntegerInputs(), 0);
    setTimeout(() => this.enhanceIntegerInputs(), 120);
  }

  private destroyCompiledElement(): void {
    if (this.compiledElement && this.ng) {
      this.ng.element(this.compiledElement).remove();
      this.compiledElement = null;
    }
  }

  private normalizeConfig(config: any): any {
    const resultado = normalizeCpaGridConfig(config);
    this.selectValueByTitle = resultado.selectValueByTitle;
    this.maskTypeByTitle = resultado.maskTypeByTitle;
    this.temporalTypeByTitle = resultado.temporalTypeByTitle;
    this.temporalFormatByTitle = resultado.temporalFormatByTitle;
    this.decimalFormatByTitle = resultado.decimalFormatByTitle;
    return resultado.config;
  }

  private syncSelectVisualState(): void {
    this.selectSync.syncSelectVisualState(this.hostRef?.nativeElement, this.selectValueByTitle);
  }

  private enhanceMultipleSelects(): void {
    this.selectSync.enhanceMultipleSelects(this.hostRef?.nativeElement);
  }

  private renderSingleSelectFallback(): void {
    this.selectFallback.renderSingleSelectFallback(this.hostRef?.nativeElement);
  }

  private renderMultipleFallbackChips(): void {
    this.selectFallback.renderMultipleFallbackChips(this.hostRef?.nativeElement);
  }

  private enhanceDateInputs(): void {
    this.fieldMask.enhanceDateInputs(this.hostRef?.nativeElement, this.temporalTypeByTitle);
  }

  private enhanceIntegerInputs(): void {
    this.fieldMask.enhanceIntegerInputs(this.hostRef?.nativeElement);
  }

  private enhanceCpfCnpjInputs(): void {
    this.fieldMask.enhanceCpfCnpjInputs(this.hostRef?.nativeElement, this.maskTypeByTitle);
  }

  private enhanceTemporalTextMasks(): void {
    this.fieldMask.enhanceTemporalTextMasks(this.hostRef?.nativeElement, this.temporalTypeByTitle, this.temporalFormatByTitle);
  }

  private enhanceDecimalInputs(): void {
    this.fieldMask.enhanceDecimalInputs(this.hostRef?.nativeElement, this.decimalFormatByTitle);
  }

  private bindRequiredValidationVisualState(): void {
    this.requiredValidation.bindRequiredValidationVisualState(this.hostRef?.nativeElement);
  }

  private emitRequiredFieldsValidity(): void {
    this.requiredValidation.emitRequiredFieldsValidity(this.hostRef?.nativeElement);
  }

  private annotateRequiredToggleTitles(): void {
    this.requiredValidation.annotateRequiredToggleTitles(this.hostRef?.nativeElement, this.scope?.config);
  }
}
